import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/SupabaseAuthContext';
// Supabaseではパスワード変更はメール経由で行われます

function Profile() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handlePasswordReset = async () => {
    if (!currentUser?.email) return;
    
    setLoading(true);
    setMessage(null);
    
    try {
      // Supabaseのパスワードリセット機能を使用
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase.auth.resetPasswordForEmail(currentUser.email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      
      if (error) throw error;
      
      setMessage({
        type: 'success',
        text: 'パスワードリセットメールを送信しました。メールを確認してください。'
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: 'パスワードリセットメールの送信に失敗しました。'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!currentUser) return;
    
    if (newPassword !== confirmPassword) {
      setMessage({
        type: 'error',
        text: '新しいパスワードが一致しません。'
      });
      return;
    }
    
    if (newPassword.length < 6) {
      setMessage({
        type: 'error',
        text: 'パスワードは6文字以上で設定してください。'
      });
      return;
    }
    
    setLoading(true);
    setMessage(null);
    
    try {
      // Supabaseのパスワード更新機能を使用
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      setMessage({
        type: 'success',
        text: 'パスワードを変更しました。'
      });
      setPasswordDialogOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: 'パスワードの変更に失敗しました。'
      });
    } finally {
      setLoading(false);
    }
  };

  // システム管理者のロールを判定
  const getSystemRole = (email: string) => {
    const adminRoles = {
      'masakazu.ohno@bitgrit.net': 'スーパー管理者',
      'rakd0930@gmail.com': 'スーパー管理者',
      'ohno@atfreaks.com': 'マネージャー'
    };
    return adminRoles[email as keyof typeof adminRoles] || '管理者';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        プロフィール
      </Typography>
      
      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Stack alignItems="center" spacing={2}>
                <Avatar sx={{ width: 100, height: 100, bgcolor: 'primary.main' }}>
                  <PersonIcon sx={{ fontSize: 60 }} />
                </Avatar>
                <Typography variant="h6">
                  {currentUser?.full_name || currentUser?.email?.split('@')[0]}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {currentUser?.email}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              アカウント情報
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="メールアドレス"
                  value={currentUser?.email || ''}
                  InputProps={{
                    readOnly: true,
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="システムロール"
                  value={getSystemRole(currentUser?.email || '')}
                  InputProps={{
                    readOnly: true,
                    startAdornment: <AdminIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="ユーザーID"
                  value={currentUser?.id || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                セキュリティ
              </Typography>
              
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<EmailIcon />}
                  onClick={handlePasswordReset}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : 'パスワードリセットメール送信'}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<LockIcon />}
                  onClick={() => setPasswordDialogOpen(true)}
                >
                  直接パスワード変更
                </Button>
              </Stack>
              
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                ※ セキュリティのため、パスワードリセットメールの利用を推奨します
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* パスワード変更ダイアログ */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>パスワード変更</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              type="password"
              label="新しいパスワード"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="password"
              label="新しいパスワード（確認）"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handlePasswordChange}
            variant="contained"
            disabled={loading || !newPassword || !confirmPassword}
          >
            {loading ? <CircularProgress size={20} /> : '変更'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Profile;